import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import {
  PlayIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

interface MigrationResult {
  success: boolean;
  dryRun: boolean;
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  errors: Array<{ collaboratorId: string; error: string }>;
  timestamp: string;
}

const SalaryDataMigration: React.FC = () => {
  const [status, setStatus] = useState<string>('');
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [lastAction, setLastAction] = useState<'dry-run' | 'execute' | null>(null);

  const handleMigration = async (isDryRun: boolean = true) => {
    setLoading(true);
    setStatus(isDryRun ? 'Executando simulação...' : 'Executando migração...');
    setLastAction(isDryRun ? 'dry-run' : 'execute');
    setResult(null);

    try {
      // Check if user is logged in
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setStatus('Erro: Usuário não autenticado');
        setLoading(false);
        return;
      }

      // Get the migration function
      const functions = getFunctions();
      // Ensure we're using the correct region if needed
      // const functions = getFunctions(undefined, 'us-central1');
      const migrateSalaryData = httpsCallable<
        { dryRun: boolean; batchSize?: number },
        MigrationResult
      >(functions, 'migrateSalaryData');

      // Call the function
      const response = await migrateSalaryData({
        dryRun: isDryRun,
        batchSize: 500
      });

      setResult(response.data);

      if (response.data.success) {
        setStatus(isDryRun ? 'Simulação concluída!' : 'Migração concluída com sucesso!');
      } else {
        setStatus('Processo concluído com erros');
      }
    } catch (error: any) {
      console.error('Migration error:', error);
      setStatus(`Erro: ${error.message}`);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (loading) {
      return <ArrowPathIcon className="h-5 w-5 animate-spin" />;
    }
    if (result?.success) {
      return <CheckCircleIcon className="h-5 w-5" />;
    }
    if (status.includes('Erro')) {
      return <ExclamationCircleIcon className="h-5 w-5" />;
    }
    return null;
  };

  const getStatusColor = () => {
    if (result?.success) return 'text-green-600 bg-green-50';
    if (status.includes('Erro')) return 'text-red-600 bg-red-50';
    return 'text-blue-600 bg-blue-50';
  };

  return (
    <div className="space-y-6">
      {/* Migration Description */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Sobre esta Migração
        </h3>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start">
            <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
            <div>
              <strong>O que faz:</strong> Move 9 campos de salário do documento principal
              para subcoleções segregadas com controle de acesso baseado em papéis.
            </div>
          </div>
          <div className="flex items-start">
            <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
            <div>
              <strong>Papéis suportados:</strong>
              <ul className="mt-1 ml-4 list-disc">
                <li><code>admin</code> - Acesso total (visualizar, editar, deletar)</li>
                <li><code>salary_admin</code> - Visualizar e editar</li>
                <li><code>salary_view</code> - Somente visualização</li>
              </ul>
            </div>
          </div>
          <div className="flex items-start">
            <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
            <div>
              <strong>Segurança:</strong> A migração é idempotente - pode ser executada
              múltiplas vezes com segurança. Colaboradores já migrados são ignorados.
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <button
          onClick={() => handleMigration(true)}
          disabled={loading}
          className={`
            inline-flex items-center px-4 py-2 border border-gray-300
            rounded-md shadow-sm text-sm font-medium text-gray-700
            bg-white hover:bg-gray-50 focus:outline-none focus:ring-2
            focus:ring-offset-2 focus:ring-blue-500
            ${loading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <EyeIcon className="h-4 w-4 mr-2" />
          Executar Simulação (Dry Run)
        </button>

        <button
          onClick={() => {
            if (window.confirm(
              'Tem certeza que deseja executar a migração?\n\n' +
              'Esta ação irá:\n' +
              '• Mover dados salariais para subcoleções\n' +
              '• Remover campos do documento principal\n' +
              '• Criar trilha de auditoria\n\n' +
              'Recomenda-se executar uma simulação primeiro.'
            )) {
              handleMigration(false);
            }
          }}
          disabled={loading}
          className={`
            inline-flex items-center px-4 py-2 border border-transparent
            rounded-md shadow-sm text-sm font-medium text-white
            bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2
            focus:ring-offset-2 focus:ring-blue-500
            ${loading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <PlayIcon className="h-4 w-4 mr-2" />
          Executar Migração
        </button>
      </div>

      {/* Status Message */}
      {status && (
        <div className={`rounded-lg p-4 ${getStatusColor()}`}>
          <div className="flex items-center">
            {getStatusIcon()}
            <span className="ml-2 font-medium">{status}</span>
          </div>
        </div>
      )}

      {/* Migration Results */}
      {result && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">
                Resultado da {result.dryRun ? 'Simulação' : 'Migração'}
              </h4>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                {showDetails ? 'Ocultar' : 'Mostrar'} Detalhes
              </button>
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {result.processedCount}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Colaboradores {result.dryRun ? 'a serem processados' : 'processados'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {result.skippedCount}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Colaboradores ignorados
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">
                  {result.failedCount}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Falhas
                </div>
              </div>
            </div>

            {/* Timestamp */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-500">
                <ClockIcon className="h-4 w-4 mr-2" />
                Executado em: {new Date(result.timestamp).toLocaleString('pt-BR')}
              </div>
            </div>

            {/* Detailed Results */}
            {showDetails && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Dados Completos:
                  </h5>
                  <pre className="text-xs text-gray-600 overflow-auto">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>

                {/* Errors */}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-4">
                    <h5 className="font-medium text-red-600 mb-2">
                      Erros Encontrados:
                    </h5>
                    <div className="space-y-2">
                      {result.errors.map((error, index) => (
                        <div
                          key={index}
                          className="bg-red-50 border border-red-200 rounded p-3 text-sm"
                        >
                          <strong>Colaborador {error.collaboratorId}:</strong>
                          <span className="ml-2 text-red-600">{error.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next Steps */}
      {result && result.success && !result.dryRun && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="text-lg font-medium text-green-900 mb-3">
            ✅ Migração Concluída com Sucesso!
          </h4>
          <div className="text-sm text-green-700 space-y-2">
            <p><strong>Próximos passos:</strong></p>
            <ol className="list-decimal list-inside ml-4 space-y-1">
              <li>Verificar os dados migrados no Firebase Console</li>
              <li>Testar o acesso com diferentes papéis de usuário</li>
              <li>Confirmar que os campos de salário aparecem corretamente na interface</li>
              <li>Monitorar logs para quaisquer erros de permissão</li>
            </ol>
          </div>
        </div>
      )}

      {/* Rollback Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ArrowPathIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-900">
              Informações de Rollback
            </h4>
            <div className="mt-1 text-sm text-blue-700">
              <p>
                Se necessário, a migração pode ser revertida usando a função
                <code className="mx-1 px-1 py-0.5 bg-blue-100 rounded">
                  rollbackSalaryMigration
                </code>
                no Firebase Functions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryDataMigration;